import { memo, useMemo } from 'react';
import { atom, useAtomValue } from 'jotai';
import { Layer, Source } from 'react-map-gl';
import type {
  SymbolLayout,
  Expression,
  CirclePaint,
  FillPaint,
} from 'mapbox-gl';
import { circlePaint, textSizeInterpolate } from 'components/map/utils';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import { buildColorByExpression } from '../color-by-utils';
import { buildTileFilterParams } from './build-tile-filter-params';
import {
  MATCH_NONE,
  buildTextFieldExpr,
  clusterCountLayerId,
  docLayerId,
  outlineLayerId,
  haloLayerId,
} from './tile-layer-utils';

const SOURCE_LAYER = 'hits';
// For datasets without a fill opacity multiplier (every one but Subsurface Maps).
const NO_FILL_OPACITY = atom<unknown[] | null>(null);
// The geotile_grid aggregation layer the ES _mvt tile emits alongside `hits`;
// each feature carries `_count`, the true number of features in that grid cell.
const AGGS_SOURCE_LAYER = 'aggs';

// Only badge grid cells holding more than one document, so single dots stay clean.
const CLUSTER_COUNT_FILTER: unknown[] = ['>', ['get', '_count'], 1];

// Amber highlight for the row→map selected feature (not a Carbon token).
const HIGHLIGHT_FILL = '#ffd54f';
// highlightInOwnColor: how much stronger the selected feature's fill is.
const OWN_COLOR_HIGHLIGHT_SCALE = 1.5;

/**
 * A colour expression at full opacity, for a halo that must read however faint
 * the layer's own colour is (Areas default to 20%).
 * @param {string | Expression} color The layer's colour, flat or per feature.
 * @return {Expression} The same colour, opaque.
 */
const opaqueColor = (color: string | Expression): Expression =>
  [
    'let',
    'rgba',
    ['to-rgba', ['to-color', color]],
    [
      'rgba',
      ['at', 0, ['var', 'rgba']],
      ['at', 1, ['var', 'rgba']],
      ['at', 2, ['var', 'rgba']],
      1,
    ],
  ] as Expression;
const HIGHLIGHT_STROKE = '#8a6d00';

const nameLayout = (visible: boolean, labelFields: string[]): SymbolLayout => ({
  'symbol-placement': 'line',
  'text-field': buildTextFieldExpr(labelFields),
  'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10] as Expression,
  'text-offset': [0, -1] as [number, number],
  'text-anchor': 'center',
  'text-allow-overlap': false,
  'text-ignore-placement': true,
  'symbol-spacing': 300,
  'visibility': visible ? 'visible' : 'none',
  'symbol-z-order': 'source',
  'text-max-width': 12,
  'text-letter-spacing': 0.05,
  'symbol-sort-key': 1,
  'text-max-angle': 45,
  'text-padding': 2,
  'text-pitch-alignment': 'viewport',
});

const DynamicTileLayer = () => {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const search = useAtomValue(atoms.searchAtom);
  const color = useAtomValue(atoms.layerColorAtom);
  const colorRules = useAtomValue(atoms.colorRulesAtom);
  const visible = useAtomValue(atoms.onMapAtom);
  const highlightId = useAtomValue(atoms.highlightedItemIdAtom);
  const fillOpacityScale = useAtomValue(
    config.tileFillOpacityAtom ?? NO_FILL_OPACITY
  ) as Expression | null;
  const highlightBounds = useAtomValue(atoms.highlightedItemBoundsAtom);

  // Flat layer color, or a `match` expression when color-by rules are active.
  const colorExpr = useMemo(
    () => buildColorByExpression(colorRules, config, color),
    [colorRules, config, color]
  );

  const queryString = useMemo(
    () => buildTileFilterParams(search.currentPayload, config),
    [search.currentPayload, config]
  );

  const tilesByLayer = useMemo(
    () =>
      (config.tileLayers ?? []).map((layer) => {
        const path = layer.geoAttribute
          ? `${config.tileEndpoint}/${layer.geoAttribute}`
          : config.tileEndpoint;
        const base = `${window.location.origin}${path}/{z}/{x}/{y}`;
        // A fillOnlyHighlighted layer only ever draws the selected feature, so it
        // fetches just that document's footprint (or nothing when none is
        // picked) instead of making ES extract every geometry in the tile.
        if (layer.fillOnlyHighlighted) {
          if (!highlightId) return null;
          const params = [queryString, `ids=${encodeURIComponent(highlightId)}`]
            .filter(Boolean)
            .join('&');
          return [`${base}?${params}`];
        }
        const url = queryString ? `${base}?${queryString}` : base;
        return [url];
      }),
    [config.tileLayers, config.tileEndpoint, queryString, highlightId]
  );

  const visibility = visible ? 'visible' : 'none';

  const namePaint = useMemo(
    () => ({
      'text-color': colorExpr,
      'text-halo-color': '#ffffff',
      'text-halo-width': 2,
    }),
    [colorExpr]
  );

  // Row→map highlight: emphasize the feature whose highlightFeatureKey property
  // matches the selected row id. Both the fill footprint and the centroid circle
  // honor it, so selection is visible at overview zoom (circle) and zoomed in
  // (fill). Falls back to the base paint when the dataset opts out or nothing is
  // selected.
  const highlightMatch = useMemo<Expression | null>(() => {
    if (!config.highlightFeatureKey || !highlightId) return null;
    return [
      '==',
      ['to-string', ['get', config.highlightFeatureKey]],
      highlightId,
    ] as Expression;
  }, [config.highlightFeatureKey, highlightId]);

  // A layer's resting fill opacity (default 0.3); the highlighted feature is 0.75.
  const unscaledFillPaint = (restingOpacity: number): FillPaint => {
    if (!highlightMatch) {
      return { 'fill-color': colorExpr, 'fill-opacity': restingOpacity };
    }

    if (config.highlightInOwnColor) {
      return {
        'fill-color': colorExpr,
        'fill-opacity': [
          'case',
          highlightMatch,
          restingOpacity * OWN_COLOR_HIGHLIGHT_SCALE,
          restingOpacity,
        ] as Expression,
      };
    }

    return {
      'fill-color': [
        'case',
        highlightMatch,
        HIGHLIGHT_FILL,
        colorExpr,
      ] as Expression,
      'fill-opacity': [
        'case',
        highlightMatch,
        0.75,
        restingOpacity,
      ] as Expression,
    };
  };

  // The dataset's opacity multiplier, when set, scales the fill opacity.
  const fillPaint = (restingOpacity = 0.3): FillPaint => {
    const paint = unscaledFillPaint(restingOpacity);
    if (!fillOpacityScale) return paint;
    return {
      ...paint,
      'fill-opacity': [
        '*',
        fillOpacityScale,
        paint['fill-opacity'],
      ] as Expression,
    };
  };

  // fillOnlyHighlighted layers paint just the selected footprint in the
  // dataset's picked color (with a crisp outline), so overlapping footprints
  // never stack into a solid blob.
  const highlightOnlyFillPaint = useMemo<FillPaint>(
    () => ({
      'fill-color': colorExpr,
      'fill-opacity': 0.4,
      'fill-outline-color': colorExpr,
    }),
    [colorExpr]
  );

  const circlePaintExpr = useMemo<CirclePaint>(() => {
    const base = circlePaint(colorExpr);
    if (!highlightMatch) return base;

    return {
      ...base,
      'circle-color': [
        'case',
        highlightMatch,
        HIGHLIGHT_FILL,
        colorExpr,
      ] as Expression,
      'circle-radius': [
        'case',
        highlightMatch,
        7,
        base['circle-radius'],
      ] as Expression,
      'circle-stroke-color': HIGHLIGHT_STROKE,
      'circle-stroke-width': ['case', highlightMatch, 2, 0] as Expression,
    };
  }, [colorExpr, highlightMatch]);

  if (!config.tileLayers?.length) return null;

  return (
    <>
      {config.tileLayers.map((layer, idx) => {
        const tiles = tilesByLayer[idx];
        // A fillOnlyHighlighted layer with nothing selected requests no tiles.
        if (!tiles) return null;

        const layerKey = `${layer.geoAttribute ?? 'layer'}-${idx}`;
        // A highlight-only source is bounded to the selected footprint, so key it
        // by the selection to force a rebuild when it changes (a source's bounds
        // can't be updated in place). The layer id stays stable so hover matches.
        const sourceId = layer.fillOnlyHighlighted
          ? `dq-${layerKey}-${highlightId ?? 'none'}`
          : `dq-${layerKey}`;
        const mainLayerId = docLayerId(layer, idx);

        // Don't fetch a fillOnlyHighlighted layer's tiles until something is
        // selected — it paints nothing otherwise, and these are the expensive
        // geo_shape footprint tiles.
        if (layer.fillOnlyHighlighted && !highlightId) return null;

        // A fillOnlyHighlighted layer restricts the fill to the selected
        // feature (nothing until one is picked), preserving any base filter.
        let fillFilter = layer.filter;
        if (layer.fillOnlyHighlighted) {
          const highlightOnly = (highlightMatch ??
            MATCH_NONE) as unknown as unknown[];
          fillFilter = layer.filter
            ? ['all', layer.filter, highlightOnly]
            : highlightOnly;
        }

        return (
          <Source
            key={sourceId}
            id={sourceId}
            type="vector"
            tiles={tiles}
            minzoom={layer.minZoom}
            {...(layer.fillOnlyHighlighted && highlightBounds
              ? { bounds: highlightBounds }
              : {})}
          >
            {layer.layerType === 'line' && (
              <Layer
                id={mainLayerId}
                type="line"
                source-layer={SOURCE_LAYER}
                {...(layer.filter ? { filter: layer.filter } : {})}
                paint={{
                  'line-color': colorExpr,
                  'line-width': 1.5,
                  ...(layer.dashArray
                    ? { 'line-dasharray': layer.dashArray }
                    : {}),
                }}
                layout={{
                  visibility,
                  ...(layer.sortKey
                    ? { 'line-sort-key': layer.sortKey as Expression }
                    : {}),
                }}
              />
            )}
            {layer.layerType === 'circle' && (
              <Layer
                id={mainLayerId}
                type="circle"
                source-layer={SOURCE_LAYER}
                {...(layer.filter ? { filter: layer.filter } : {})}
                paint={circlePaintExpr}
                layout={{ visibility }}
              />
            )}
            {layer.layerType === 'fill' && (
              <Layer
                id={mainLayerId}
                type="fill"
                source-layer={SOURCE_LAYER}
                {...(fillFilter ? { filter: fillFilter } : {})}
                paint={
                  layer.fillOnlyHighlighted
                    ? highlightOnlyFillPaint
                    : fillPaint(layer.fillOpacity)
                }
                layout={{
                  visibility,
                  ...(layer.sortKey
                    ? { 'fill-sort-key': layer.sortKey as Expression }
                    : {}),
                }}
              />
            )}
            {layer.layerType === 'fill' && layer.outline && (
              <Layer
                id={outlineLayerId(layer, idx)}
                type="line"
                source-layer={SOURCE_LAYER}
                {...(layer.filter ? { filter: layer.filter } : {})}
                paint={{
                  'line-color': colorExpr,
                  'line-width': 1.5,
                  ...(layer.outline.opacity !== undefined
                    ? { 'line-opacity': layer.outline.opacity }
                    : {}),
                }}
                layout={{
                  visibility,
                  ...(layer.sortKey
                    ? { 'line-sort-key': layer.sortKey as Expression }
                    : {}),
                }}
              />
            )}
            {layer.layerType === 'fill' &&
              layer.outline &&
              config.highlightInOwnColor &&
              highlightMatch && (
                // The selected feature's halo, in its own colour at full opacity.
                <Layer
                  id={haloLayerId(layer, idx)}
                  type="line"
                  source-layer={SOURCE_LAYER}
                  filter={
                    layer.filter
                      ? ['all', layer.filter, highlightMatch]
                      : highlightMatch
                  }
                  paint={{
                    'line-color': opaqueColor(colorExpr),
                    'line-width': 4,
                    'line-blur': 2,
                  }}
                  layout={{ visibility }}
                />
              )}
            {layer.layerType === 'symbol' && (
              <Layer
                id={mainLayerId}
                type="symbol"
                source-layer={SOURCE_LAYER}
                {...(layer.filter ? { filter: layer.filter } : {})}
                layout={{
                  'text-field': layer.textField as Expression,
                  'text-allow-overlap': true,
                  'text-size': (layer.textSize ??
                    textSizeInterpolate) as Expression,
                  'visibility': visibility,
                }}
                paint={{ 'text-color': colorExpr }}
              />
            )}
            {layer.labelFields && layer.labelMinZoom && (
              <Layer
                id={`${sourceId}-names`}
                type="symbol"
                source-layer={SOURCE_LAYER}
                minzoom={layer.labelMinZoom}
                layout={nameLayout(visible, layer.labelFields)}
                paint={namePaint}
              />
            )}
            {layer.clusterCount && (
              <Layer
                id={clusterCountLayerId(layer, idx)}
                type="fill"
                source-layer={AGGS_SOURCE_LAYER}
                filter={CLUSTER_COUNT_FILTER}
                // Invisible hit-target: carries `_count` so the hover popup can
                // read how many documents stack on a dot, with no visible badge.
                paint={{ 'fill-opacity': 0 }}
                layout={{ visibility }}
              />
            )}
          </Source>
        );
      })}
    </>
  );
};

const MemoizedDynamicTileLayer = memo(DynamicTileLayer);
export { MemoizedDynamicTileLayer as DynamicTileLayer };
